import type { Lang } from "../index";

export interface NoPluginMeetingTranscriptionCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: NoPluginMeetingTranscriptionCopy = {
  seo: {
    title: "Meeting transcription with no plugin, no bot, no extension",
    description:
      "Transcribe meetings without a Chrome extension, a calendar bot, or anything to install on the other side. One signed desktop app captures system audio for Zoom, Teams, Meet, FaceTime — done.",
  },
  eyebrow: "Use case · No plugin, no bot",
  h1: "No Chrome extension. No bot in the call. No install on their side.",
  lede:
    "Most transcription tools ship as a Chrome extension plus a calendar bot plus a meeting joiner. Three install steps, three IT reviews, and the whole thing breaks the moment your call isn't a browser tab. Minuta is one signed desktop app — it captures the audio your computer is already playing and gives you the text.",
  problem: {
    heading: "The install stack nobody asked for",
    body1:
      "Every cloud transcription product has the same onboarding stack: install a Chrome extension, give it calendar access, invite their bot to your meetings, ask the other side to accept it. Each step is a small friction; together they're the reason most people just don't transcribe at all. And if your IT blocks extensions, or the call is on FaceTime, or you're on a native Zoom client without an integration, the whole product just doesn't apply.",
    body2:
      "The actual functionality you want is simple: capture the audio that's already coming out of your computer speakers, run it through a transcription model, give you the text. Everything else — the Chrome extension, the calendar OAuth, the meeting bot — exists because the products were architected for the cloud era when local system audio capture wasn't viable. It is now.",
  },
  scenarios: {
    heading: "Where it actually matters",
    items: [
      {
        tag: "Locked-down Mac",
        title: "\"IT blocks every recorder you've ever tried\"",
        body: "Your work Mac has Chrome extensions disabled by MDM. Otter and Tactiq are non-starters. Calendar bot integrations are blocked. Minuta is a single signed desktop app — install via the standard installer, no admin extension, no bot, no calendar OAuth. The IT review takes 10 minutes instead of 6 weeks.",
      },
      {
        tag: "FaceTime / native app",
        title: "\"Calls that aren't browser tabs\"",
        body: "Customer call on FaceTime. Or a podcast recording in QuickTime. Or a native Zoom client without the latest SDK. None of these work with a Chrome extension. Minuta captures system audio — whatever is playing through your computer, transcribed in real time. Doesn't matter what app made the sound.",
      },
      {
        tag: "Anti-friction / cross-team",
        title: "\"You don't ask the other side to install anything\"",
        body: "Five-person customer call. With Otter you'd ask them to accept Otter joining as a bot, or you'd send them a transcription link they have to log into. With Minuta there is nothing on their side. Your Mac records the system audio that comes through your speakers; their experience is identical to a normal call.",
      },
    ],
  },
  how: {
    heading: "How Minuta plugs in without plugging into anything",
    body:
      "Open Minuta before the call. Run the call however you'd run it — Zoom web, Zoom native, Teams, Meet, FaceTime, Phone via Continuity, podcast recorder, browser tab playing a webinar. Minuta listens to your system audio and transcribes in real time.",
    bullets: [
      "One signed desktop app. Standard installer, no admin install, no Chrome extension. IT review is the standard signed-app review.",
      "Captures system audio at the OS level. Works on Zoom (web or native), Teams (web or native), Meet, FaceTime, Phone (Continuity), native podcast recorders, anything that plays sound through your computer.",
      "No bot joins the call. The other side sees nothing because nothing third-party is in the room.",
      "No calendar OAuth, no contact list scraping, no integration permissions. The app reads your microphone + system audio and that's it.",
    ],
  },
  proof: {
    heading: "Why this is structurally different from the alternatives",
    bullets: [
      "Otter requires a Chrome extension and a calendar bot for the auto-record flow. Tactiq is Chrome-only by architecture. tl;dv joins as a bot every time. Each is a different friction profile; all three break in IT-locked or non-browser environments.",
      "A signed desktop app is a one-time IT review. A Chrome extension + calendar OAuth + meeting bot is three separate reviews — and the bot one is often a hard no in security-conscious orgs.",
      "The user's privacy posture is \"I don't have to ask anyone else to install something\" — that's true zero-friction onboarding, not just the user's own zero-install but the whole call's.",
      "Free tier 1h/month — test it on a real call without commitment, including the calls a Chrome extension would never have helped you with.",
    ],
  },
  cta: {
    heading: "Try it without installing anything else",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: NoPluginMeetingTranscriptionCopy = {
  seo: {
    title: "Transcrição de reunião sem plugin, sem bot, sem extensão",
    description:
      "Transcrição reunião sem plugin, sem extensão Chrome, sem instalar bot do calendário. Um app desktop assinado captura o áudio do sistema no Zoom, Teams, Meet, FaceTime — só isso.",
  },
  eyebrow: "Caso de uso · Sem plugin, sem bot",
  h1: "Sem extensão do Chrome. Sem bot na call. Sem instalar nada do outro lado.",
  lede:
    "A maioria das ferramentas de transcrição vem como extensão do Chrome + bot do calendário + joiner de reunião. Três etapas de instalação, três reviews de IT, e tudo desmonta no momento em que a sua call não é uma aba do navegador. O Minuta é um app desktop assinado, só isso — captura o áudio que o seu computador já está tocando e devolve o texto.",
  problem: {
    heading: "A pilha de instalação que ninguém pediu",
    body1:
      "Todo produto de transcrição na nuvem tem o mesmo onboarding: instala uma extensão do Chrome, libera acesso ao calendário, convida o bot pra sua reunião, pede pro outro lado aceitar. Cada passo é uma fricção pequena; juntos, são a razão pela qual a maioria simplesmente não transcreve. E se o seu IT bloqueia extensões, ou a call é no FaceTime, ou você está no Zoom nativo sem integração, o produto inteiro deixa de existir pra você.",
    body2:
      "A funcionalidade que você quer é simples: capturar o áudio que já está saindo das caixas do seu computador, rodar por um modelo de transcrição, devolver o texto. Todo o resto — a extensão do Chrome, o OAuth de calendário, o bot — existe porque esses produtos foram arquitetados na era cloud, quando a captura local de áudio do sistema não era viável. Agora é.",
  },
  scenarios: {
    heading: "Onde isso pesa de verdade",
    items: [
      {
        tag: "computador corporativo travado",
        title: "\"IT bloqueia tudo que você já tentou usar\"",
        body: "O seu computador de trabalho tem extensão do Chrome desabilitada via MDM. Otter e Tactiq são impossíveis. Integração com calendar bot bloqueada. O Minuta é um app desktop assinado, instala pelo instalador normal — sem extensão de admin, sem bot, sem OAuth de calendário. O review do IT leva 10 minutos em vez de 6 semanas.",
      },
      {
        tag: "FaceTime / app nativo",
        title: "\"Calls que não são aba de navegador\"",
        body: "Call de cliente no FaceTime. Ou gravação de podcast no QuickTime. Ou Zoom nativo sem o SDK mais recente. Nada disso funciona com extensão do Chrome. O Minuta captura áudio do sistema — o que está tocando no seu computador vira texto em tempo real. Não importa qual app gerou o som.",
      },
      {
        tag: "Anti-fricção / cross-team",
        title: "\"Você não pede pro outro lado instalar nada\"",
        body: "Call de cliente com cinco pessoas. Com Otter, você pediria pra eles aceitarem o bot do Otter na sala, ou mandaria um link de transcrição que eles têm que logar pra ver. Com Minuta, do lado deles não tem nada. O seu computador grava o áudio do sistema que sai das caixas; pra eles a experiência é igual a uma call normal.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta entra sem entrar em nada",
    body:
      "Abre o Minuta antes da call. Roda a call do jeito que você sempre roda — Zoom web, Zoom nativo, Teams, Meet, FaceTime, telefone via Continuity, gravador de podcast, aba do navegador tocando webinar. O Minuta escuta o áudio do sistema e transcreve em tempo real.",
    bullets: [
      "Um app desktop assinado, só isso. Instalador padrão, sem instalação de admin, sem extensão do Chrome. O review do IT é o review padrão de app assinado.",
      "Captura áudio do sistema no nível do OS. Funciona em Zoom (web ou nativo), Teams (web ou nativo), Meet, FaceTime, Phone (Continuity), gravadores de podcast nativos, qualquer coisa que toque som no seu computador.",
      "Nenhum bot entra na call. O outro lado não vê nada, porque nada de terceiros está na sala.",
      "Sem OAuth de calendário, sem leitura de contatos, sem permissões de integração. O app lê o seu microfone + áudio do sistema e ponto.",
    ],
  },
  proof: {
    heading: "Por que isso é estruturalmente diferente das alternativas",
    bullets: [
      "Otter precisa de extensão do Chrome e bot do calendário pro fluxo de auto-record. Tactiq é Chrome-only por arquitetura. tl;dv entra como bot toda vez. Cada um tem um perfil de fricção diferente; os três quebram em ambiente travado pelo IT ou em call que não é navegador.",
      "App Mac assinado é um review de IT, uma vez. Extensão do Chrome + OAuth de calendário + bot de reunião são três reviews separados — e o do bot costuma ser não definitivo em empresas com security maduro.",
      "A postura do usuário é \"eu não tenho que pedir pra ninguém instalar nada\" — isso é onboarding sem fricção de verdade, não só zero-install do seu lado, mas zero-install da call inteira.",
      "1h/mês no plano grátis — testa numa call de verdade sem compromisso, inclusive nas calls que extensão do Chrome nunca ia te ajudar.",
    ],
  },
  cta: {
    heading: "Testa sem precisar instalar mais nada",
    body: "7 dias com tudo desbloqueado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: NoPluginMeetingTranscriptionCopy = {
  seo: {
    title: "Transcripción de reunión sin plugin, sin bot, sin extensión",
    description:
      "Transcripción reunión sin plugin, sin extensión Chrome, sin instalar bot del calendario. Una app desktop firmada captura el audio del sistema en Zoom, Teams, Meet, FaceTime — y ya.",
  },
  eyebrow: "Caso de uso · Sin plugin, sin bot",
  h1: "Sin extensión de Chrome. Sin bot en la call. Sin instalar nada del otro lado.",
  lede:
    "La mayoría de las herramientas de transcripción vienen como extensión de Chrome + bot del calendario + joiner de reunión. Tres pasos de instalación, tres reviews de IT, y todo se rompe en el momento en que tu call no es una pestaña del navegador. Minuta es una app desktop firmada y nada más — captura el audio que tu computadora ya está reproduciendo y te devuelve el texto.",
  problem: {
    heading: "El stack de instalación que nadie pidió",
    body1:
      "Todo producto de transcripción en la nube tiene el mismo onboarding: instalá una extensión de Chrome, dale acceso al calendario, invitá a su bot a tu reunión, pedile al otro lado que lo acepte. Cada paso es una fricción pequeña; juntos son la razón por la cual la mayoría simplemente no transcribe. Y si tu IT bloquea extensiones, o la call es por FaceTime, o estás en el Zoom nativo sin integración, el producto entero deja de aplicar.",
    body2:
      "La funcionalidad que querés es simple: capturar el audio que ya está saliendo de los parlantes de tu computadora, pasarlo por un modelo de transcripción, devolverte el texto. Todo lo demás — la extensión de Chrome, el OAuth de calendario, el bot de reunión — existe porque esos productos fueron arquitectados en la era cloud, cuando la captura local de audio del sistema no era viable. Ahora sí lo es.",
  },
  scenarios: {
    heading: "Dónde pesa de verdad",
    items: [
      {
        tag: "Computadora corporativa bloqueada",
        title: "\"IT bloquea cada grabador que probaste\"",
        body: "Tu Mac de trabajo tiene las extensiones de Chrome deshabilitadas por MDM. Otter y Tactiq son imposibles. La integración con calendar bot está bloqueada. Minuta es una sola app Mac firmada — se instala con el instalador estándar, sin extensión de admin, sin bot, sin OAuth de calendario. El review de IT toma 10 minutos en vez de 6 semanas.",
      },
      {
        tag: "FaceTime / app nativa",
        title: "\"Calls que no son pestañas del navegador\"",
        body: "Call de cliente por FaceTime. O grabación de podcast en QuickTime. O Zoom nativo sin el SDK más reciente. Nada de eso funciona con una extensión de Chrome. Minuta captura el audio del sistema — lo que esté reproduciéndose en tu computadora, transcrito en tiempo real. No importa qué app generó el sonido.",
      },
      {
        tag: "Anti-fricción / cross-team",
        title: "\"No le pedís al otro lado que instale nada\"",
        body: "Call de cliente con cinco personas. Con Otter les pedirías que acepten al bot de Otter entrando a la sala, o les mandarías un link de transcripción al que tienen que loguearse. Con Minuta, del lado de ellos no hay nada. Tu Mac graba el audio del sistema que sale por los parlantes; para ellos la experiencia es idéntica a una call normal.",
      },
    ],
  },
  how: {
    heading: "Cómo entra Minuta sin entrar en nada",
    body:
      "Abrí Minuta antes de la call. Corré la call como siempre — Zoom web, Zoom nativo, Teams, Meet, FaceTime, teléfono vía Continuity, grabador de podcast, pestaña del navegador reproduciendo un webinar. Minuta escucha el audio del sistema y transcribe en tiempo real.",
    bullets: [
      "Una sola app Mac firmada. Instalador estándar, sin instalación de admin, sin extensión de Chrome. El review de IT es el review estándar de app firmada.",
      "Captura el audio del sistema a nivel del OS. Funciona en Zoom (web o nativo), Teams (web o nativo), Meet, FaceTime, Phone (Continuity), grabadores de podcast nativos, cualquier cosa que reproduzca sonido en tu computadora.",
      "Ningún bot entra a la call. El otro lado no ve nada, porque no hay nada de terceros en la sala.",
      "Sin OAuth de calendario, sin scraping de contactos, sin permisos de integración. La app lee tu micrófono + audio del sistema, y nada más.",
    ],
  },
  proof: {
    heading: "Por qué esto es estructuralmente distinto a las alternativas",
    bullets: [
      "Otter requiere extensión de Chrome y bot del calendario para el flujo de auto-record. Tactiq es Chrome-only por arquitectura. tl;dv entra como bot siempre. Cada uno tiene un perfil de fricción distinto; los tres se rompen en entornos bloqueados por IT o en calls que no son navegador.",
      "Una app Mac firmada es un review de IT, una sola vez. Extensión de Chrome + OAuth de calendario + bot de reunión son tres reviews separados — y el del bot suele ser un \"no\" rotundo en organizaciones con security maduro.",
      "La postura del usuario es \"no tengo que pedirle a nadie más que instale nada\" — eso es onboarding sin fricción de verdad, no solo zero-install de tu lado, sino zero-install de la call entera.",
      "1h/mes en el plan gratis — probalo en una call real sin compromiso, incluidas las calls en las que una extensión de Chrome nunca te iba a ayudar.",
    ],
  },
  cta: {
    heading: "Probalo sin instalar nada más",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const noPluginMeetingTranscriptionCopy: Record<Lang, NoPluginMeetingTranscriptionCopy> = { en, pt, es };
